import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { categories, heroImages } from "../../data/productData";

const CEILING_LINK = "ceiling_cladding";

const NewProductsPage = () => {
  const [current, setCurrent] = useState(0);
  const [expanded, setExpanded] = useState({});
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const scrollRef = useRef(null);

  const checkArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 4);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  const setCeilingRef = useCallback(
    (node) => {
      if (scrollRef.current) {
        scrollRef.current.removeEventListener("scroll", checkArrows);
      }
      scrollRef.current = node;
      if (node) {
        node.addEventListener("scroll", checkArrows);
        requestAnimationFrame(checkArrows);
      }
    },
    [checkArrows]
  );

  // Re-measure when items are added/removed (expand/collapse) or window resizes
  useEffect(() => {
    requestAnimationFrame(checkArrows);
  }, [expanded, checkArrows]);

  useEffect(() => {
    window.addEventListener("resize", checkArrows);
    return () => window.removeEventListener("resize", checkArrows);
  }, [checkArrows]);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 290, behavior: "smooth" });
  };

  // Hero auto-advance
  useEffect(() => {
    const t = setInterval(
      () => setCurrent((c) => (c + 1) % heroImages.length),
      5000
    );
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-800 pb-6">
      {/* Hero Carousel */}
      <div className="relative h-[480px] md:h-[620px] overflow-hidden">
        {heroImages.map((img, i) => (
          <div
            key={i}
            className={`absolute inset-0 bg-center bg-cover transition-opacity duration-700 ${
              current === i ? "opacity-100" : "opacity-0"
            }`}
            style={{ backgroundImage: `url(${img})` }}>
            <div className="absolute inset-0 bg-black/40 flex items-center">
              <div className="max-w-5xl px-6 lg:px-12 text-left text-white">
                <h1 className="text-3xl md:text-6xl font-extrabold leading-tight">Our Products</h1>
                <p className="mt-4 max-w-2xl text-sm md:text-xl font-light">
                  Browse our extensive range of high-quality construction products and materials.
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 space-y-12">
        {/* Breadcrumbs */}
        <nav className="breadcrumbs mb-2" aria-label="Breadcrumb">
          <ol className="flex items-center">
            <li>
              <Link to="/" className="hover:underline">
                Home
              </Link>
            </li>
            <li aria-hidden>
              <svg
                className="w-4 h-4 text-gray-400 mx-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </li>
            <li className="crumb-current">Products</li>
          </ol>
        </nav>

        {/* Categories Section — each category is a vertical card with subcategories listed vertically */}
        {/* <section className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold">PRODUCT CATEGORIES</h2>
              <p className="text-sm text-gray-500">Browse categories and their subcategories.</p>
            </div>
          </div> */}

        {/* <div className="flex gap-6 overflow-x-auto py-4">
            {categories.map((cat, i) => (
              <div key={i}
                className="flex-shrink-0 w-56 bg-white rounded-md border shadow-sm">
                <div className="h-28 bg-gray-50 overflow-hidden rounded-t-md">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-semibold mb-2">{cat.name}</h3>
                  <div className="space-y-2 max-h-40 overflow-auto">
                    {cat.subcategories.map((s, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3">
                        <div className="w-10 h-8 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                          <img
                            src={s.image}
                            alt={s.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="text-xs text-gray-700">{s.name}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3">
                    <Link
                      to={`/our-products/${cat.link}`}
                      className="text-red-500 text-sm font-semibold">
                      View All
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div> */}
        {/* </section> */}

        {/* Products Section */}
        {categories.map((cat, i) => {
          const limit = 4;
          const isExpanded = !!expanded[cat.link];
          const displayed = isExpanded
            ? cat.subcategories
            : cat.subcategories.slice(0, limit);
          const isCeiling = cat.link === CEILING_LINK;

          return (
            <section
              key={i}
              className="bg-white p-4 md:p-8 rounded-lg shadow-sm border hover:shadow-lg transition-shadow duration-300">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
                <div className="min-w-0">
                  <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-2 md:mb-4">
                    {cat.name}
                  </h2>
                  <p className="text-sm sm:text-base md:text-xl text-gray-500">
                    {cat.desc}
                  </p>
                </div>
                <Link
                  to={`/our-products/${cat.link}`}
                  className="btn-accent-fill px-3 sm:px-4 py-2 rounded font-semibold transition-all duration-300 text-sm sm:text-base whitespace-nowrap flex-shrink-0">
                  <span>EXPLORE {cat.name.toUpperCase()}</span>
                </Link>
              </div>

              {isCeiling ? (
                <div style={{ position: "relative" }}>

                  {showLeft && (
                    <button
                      onClick={() => scroll(-1)}
                      className="btn-accent"
                      style={{
                        position: "absolute",
                        left: 8,
                        top: "43%",
                        transform: "translateY(-50%)",
                        zIndex: 20,
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        border: "none",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
                        cursor: "pointer",
                      }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}

                  <div
                    ref={setCeilingRef}
                    style={{
                      display: "flex",
                      gap: "24px",
                      overflowX: "auto",
                      padding: "16px 4px",
                      scrollBehavior: "smooth",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    }}>
                    {displayed.map((s, idx) => {
                      const subSlug = String(s.slug || s.id || s.name)
                        .toLowerCase()
                        .replace(/\s+/g, "-")
                        .replace(/[^a-z0-9-]/g, "");
                      return (
                        <Link
                          key={idx}
                          to={`/our-products/${cat.link}/subcategory/${subSlug}`}
                          className="flex-shrink-0 w-48 sm:w-56 md:w-60 text-center focus:outline-none focus:ring-2 focus:ring-[#00a3a1] rounded-md group cursor-pointer transition-all duration-300 hover:scale-105">
                          <div className="w-full h-36 sm:h-40 md:h-44 bg-gray-50 rounded-md flex items-center justify-center overflow-hidden relative">
                            <img
                              src={s.image}
                              alt={s.name}
                              className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300" />
                          </div>
                          <div className="mt-3 text-base sm:text-lg font-semibold text-gray-600 group-hover:text-[#00a3a1] transition-colors duration-300">
                            {s.name.toUpperCase()}
                          </div>
                        </Link>
                      );
                    })}
                  </div>

                  {showRight && (
                    <button
                      onClick={() => scroll(1)}
                      className="btn-accent"
                      style={{
                        position: "absolute",
                        right: 8,
                        top: "43%",
                        transform: "translateY(-50%)",
                        zIndex: 20,
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        border: "none",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
                        cursor: "pointer",
                      }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                </div>
              ) : (
              
                <div className="flex gap-6 overflow-x-auto py-4">
                  {displayed.map((s, idx) => {
                    const subSlug = String(s.slug || s.id || s.name)
                      .toLowerCase()
                      .replace(/\s+/g, "-")
                      .replace(/[^a-z0-9-]/g, "");
                    return (
                      <Link
                        key={idx}
                        to={`/our-products/${cat.link}/subcategory/${subSlug}`}
                        className="flex-shrink-0 w-48 sm:w-56 md:w-60 text-center focus:outline-none focus:ring-2 focus:ring-[#00a3a1] rounded-md group cursor-pointer transition-all duration-300 hover:scale-105">
                        <div className="w-full h-36 sm:h-40 md:h-44 bg-gray-50 rounded-md flex items-center justify-center overflow-hidden relative">
                          <img
                            src={s.image}
                            alt={s.name}
                            className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300" />
                        </div>
                        <div className="mt-3 text-base sm:text-lg font-semibold text-gray-600 group-hover:text-[#00a3a1] transition-colors duration-300">
                          {s.name.toUpperCase()}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}

              {/* View More / Show Less button */}
              {cat.subcategories.length > limit && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={() =>
                      setExpanded((prev) => ({
                        ...prev,
                        [cat.link]: !prev[cat.link],
                      }))
                    }
                    className="px-6 py-3 rounded-xl font-semibold transition-all duration-300 btn-accent">
                    {isExpanded
                      ? "Show Less"
                      : `View More (${cat.subcategories.length - limit})`}
                  </button>
                </div>
              )}
            </section>
          );
        })}
        {/* Metal Profiles Section */}
        {/* <section className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold">METAL PROFILES</h2>
              <p className="text-sm text-gray-500">
                A full range of metal profiles to support your drywall partitions, ceilings and facades.
              </p>
            </div>
            <Link
              to="/products/metal-profiles"
              className="text-red-500 font-semibold">
              EXPLORE METAL PROFILES
            </Link>
          </div>

          <div className="flex gap-6 overflow-x-auto py-4">
            {metalProfiles.map((m, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-40 text-center">
                <div className="h-24 bg-gray-50 rounded-md flex items-center justify-center overflow-hidden">
                  <img
                    src={m.img}
                    alt={m.name}
                    className="object-contain h-full"
                  />
                </div>
                <div className="mt-3 text-xs text-gray-600">{m.name.toUpperCase()}</div>
              </div>
            ))}
          </div>
        </section> */}

        {/* Insulation & Accessories Row */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {smallBoxes.map((b, i) => (
            <div
              key={i}
              className="bg-white p-6 rounded-lg shadow-sm border flex items-center gap-6">
              <div className="w-32 h-28 bg-gray-50 rounded overflow-hidden flex items-center justify-center">
                <img
                  src={b.img}
                  alt={b.title}
                  className="object-contain h-full"
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold">{b.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{b.desc}</p>
                <Link
                  to={b.link}
                  className="mt-3 inline-block text-red-500 font-semibold">
                  EXPLORE {b.title.toUpperCase()}
                </Link>
              </div>
            </div>
          ))}
        </div> */}

        {/* Big CTA Image */}
        {/* <div className="relative overflow-hidden rounded-lg">
          <img
            src="/assets/productimages/cta-construction.jpg"
            alt="cta"
            className="w-full h-[380px] object-cover grayscale"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-white/90 p-6 md:p-10 max-w-md text-left">
              <h3 className="text-xl md:text-2xl font-extrabold text-red-600">SMARTER DRYWALL</h3>
              <p className="mt-2 text-gray-800 font-semibold">Boost efficiency, reduce waste, and install faster.</p>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default NewProductsPage;




// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import { categories, heroImages } from "../../data/productData";

// const NewProductsPage = () => {
//   const [current, setCurrent] = useState(0);
//   const [expanded, setExpanded] = useState({});

//   useEffect(() => {
//     const t = setInterval(() => setCurrent((c) => (c + 1) % heroImages.length), 5000);
//     return () => clearInterval(t);
//   }, []);

//   return (
//     <div className="min-h-screen bg-white text-gray-800 pb-6">
//       {/* Hero Carousel */}
//       <div className="relative h-[480px] md:h-[620px] overflow-hidden">
//         {heroImages.map((img, i) => (
//           <div
//             key={i}
//             className={`absolute inset-0 bg-center bg-cover transition-opacity duration-700 ${
//               current === i ? "opacity-100" : "opacity-0"
//             }`}
//             style={{ backgroundImage: `url(${img})` }}>
//             <div className="absolute inset-0 bg-black/40 flex items-center">
//               <div className="max-w-5xl px-6 lg:px-12 text-left text-white">
//                 <h1 className="text-3xl md:text-6xl font-extrabold leading-tight">Our Products</h1>
//                 <p className="mt-4 max-w-2xl text-sm md:text-xl font-light">
//                   Browse our extensive range of high-quality construction products and materials.
//                 </p>
//               </div>
//             </div>
//           </div>
//         ))}
//       </div>

//       <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 space-y-12">
//         {/* Breadcrumbs */}
//         <nav
//           className="breadcrumbs mb-2"
//           aria-label="Breadcrumb">
//           <ol className="flex items-center">
//             <li>
//               <Link
//                 to="/"
//                 className="hover:underline">
//                 Home
//               </Link>
//             </li>
//             <li aria-hidden>
//               <svg
//                 className="w-4 h-4 text-gray-400 mx-2"
//                 fill="none"
//                 stroke="currentColor"
//                 viewBox="0 0 24 24">
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth={2}
//                   d="M9 5l7 7-7 7"
//                 />
//               </svg>
//             </li>
//             <li className="crumb-current">Products</li>
//           </ol>
//         </nav>
//         {/* Categories Section — each category is a vertical card with subcategories listed vertically */}
//         {/* <section className="bg-white p-6 rounded-lg shadow-sm border">
//           <div className="flex items-center justify-between mb-6">
//             <div>
//               <h2 className="text-2xl font-bold">PRODUCT CATEGORIES</h2>
//               <p className="text-sm text-gray-500">Browse categories and their subcategories.</p>
//             </div>
//           </div> */}

//         {/* <div className="flex gap-6 overflow-x-auto py-4">
//             {categories.map((cat, i) => (
//               <div key={i}
//                 className="flex-shrink-0 w-56 bg-white rounded-md border shadow-sm">
//                 <div className="h-28 bg-gray-50 overflow-hidden rounded-t-md">
//                   <img
//                     src={cat.image}
//                     alt={cat.name}
//                     className="w-full h-full object-cover"
//                   />
//                 </div>
//                 <div className="p-3">
//                   <h3 className="text-sm font-semibold mb-2">{cat.name}</h3>
//                   <div className="space-y-2 max-h-40 overflow-auto">
//                     {cat.subcategories.map((s, idx) => (
//                       <div
//                         key={idx}
//                         className="flex items-center gap-3">
//                         <div className="w-10 h-8 bg-gray-100 rounded overflow-hidden flex-shrink-0">
//                           <img
//                             src={s.image}
//                             alt={s.name}
//                             className="w-full h-full object-cover"
//                           />
//                         </div>
//                         <div className="text-xs text-gray-700">{s.name}</div>
//                       </div>
//                     ))}
//                   </div>
//                   <div className="mt-3">
//                     <Link
//                       to={`/our-products/${cat.link}`}
//                       className="text-red-500 text-sm font-semibold">
//                       View All
//                     </Link>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div> */}
//         {/* </section> */}

//         {/* Products Section */}
//         {categories.map((cat, i) => {
//           const limit = 4;
//           const isExpanded = !!expanded[cat.link];
//           const displayed = isExpanded ? cat.subcategories : cat.subcategories.slice(0, limit);

//           return (
//             <section
//               key={i}
//               className="bg-white p-4 md:p-8 rounded-lg shadow-sm border hover:shadow-lg transition-shadow duration-300">
//               <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
//                 <div className="min-w-0">
//                   <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-2 md:mb-4">{cat.name}</h2>
//                   <p className="text-sm sm:text-base md:text-xl text-gray-500">{cat.desc}</p>
//                 </div>
//                 <Link
//                   to={`/our-products/${cat.link}`}
//                   className="btn-accent-fill px-3 sm:px-4 py-2 rounded font-semibold transition-all duration-300 text-sm sm:text-base whitespace-nowrap flex-shrink-0">
//                   <span>EXPLORE {cat.name.toUpperCase()}</span>
//                 </Link>
//               </div>

//               <div className="flex gap-6 overflow-x-auto py-4">
//                 {displayed.map((s, idx) => {
//                   const subSlug = String(s.slug || s.id || s.name)
//                     .toLowerCase()
//                     .replace(/\s+/g, "-")
//                     .replace(/[^a-z0-9-]/g, "");
//                   const to = `/our-products/${cat.link}/subcategory/${subSlug}`;
//                   return (
//                     <Link
//                       key={idx}
//                       to={to}
//                       className="flex-shrink-0 w-48 sm:w-56 md:w-60 text-center focus:outline-none focus:ring-2 focus:ring-[#00a3a1] rounded-md group cursor-pointer transition-all duration-300 hover:scale-105">
//                       <div className="w-full h-36 sm:h-40 md:h-44 bg-gray-50 rounded-md flex items-center justify-center overflow-hidden relative">
//                         <img
//                           src={s.image}
//                           alt={s.name}
//                           className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-110"
//                         />
//                         <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300" />
//                       </div>
//                       <div className="mt-3 text-base sm:text-lg font-semibold text-gray-600 group-hover:text-[#00a3a1] transition-colors duration-300">
//                         {s.name.toUpperCase()}
//                       </div>
//                     </Link>
//                   );
//                 })}
//               </div>

//               {cat.subcategories.length > limit && (
//                 <div className="mt-6 flex justify-center">
//                   <button
//                     onClick={() => setExpanded((prev) => ({ ...prev, [cat.link]: !prev[cat.link] }))}
//                     className="px-6 py-3 rounded-xl font-semibold transition-all duration-300 btn-accent">
//                     {isExpanded ? "Show Less" : `View More (${cat.subcategories.length - limit})`}
//                   </button>
//                 </div>
//               )}
//             </section>
//           );
//         })}

//         {/* Metal Profiles Section */}
//         {/* <section className="bg-white p-6 rounded-lg shadow-sm border">
//           <div className="flex items-center justify-between mb-6">
//             <div>
//               <h2 className="text-2xl font-bold">METAL PROFILES</h2>
//               <p className="text-sm text-gray-500">
//                 A full range of metal profiles to support your drywall partitions, ceilings and facades.
//               </p>
//             </div>
//             <Link
//               to="/products/metal-profiles"
//               className="text-red-500 font-semibold">
//               EXPLORE METAL PROFILES
//             </Link>
//           </div>

//           <div className="flex gap-6 overflow-x-auto py-4">
//             {metalProfiles.map((m, i) => (
//               <div
//                 key={i}
//                 className="flex-shrink-0 w-40 text-center">
//                 <div className="h-24 bg-gray-50 rounded-md flex items-center justify-center overflow-hidden">
//                   <img
//                     src={m.img}
//                     alt={m.name}
//                     className="object-contain h-full"
//                   />
//                 </div>
//                 <div className="mt-3 text-xs text-gray-600">{m.name.toUpperCase()}</div>
//               </div>
//             ))}
//           </div>
//         </section> */}

//         {/* Insulation & Accessories Row */}
//         {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//           {smallBoxes.map((b, i) => (
//             <div
//               key={i}
//               className="bg-white p-6 rounded-lg shadow-sm border flex items-center gap-6">
//               <div className="w-32 h-28 bg-gray-50 rounded overflow-hidden flex items-center justify-center">
//                 <img
//                   src={b.img}
//                   alt={b.title}
//                   className="object-contain h-full"
//                 />
//               </div>
//               <div>
//                 <h3 className="text-lg font-semibold">{b.title}</h3>
//                 <p className="text-sm text-gray-500 mt-1">{b.desc}</p>
//                 <Link
//                   to={b.link}
//                   className="mt-3 inline-block text-red-500 font-semibold">
//                   EXPLORE {b.title.toUpperCase()}
//                 </Link>
//               </div>
//             </div>
//           ))}
//         </div> */}

//         {/* Big CTA Image */}
//         {/* <div className="relative overflow-hidden rounded-lg">
//           <img
//             src="/assets/productimages/cta-construction.jpg"
//             alt="cta"
//             className="w-full h-[380px] object-cover grayscale"
//           />
//           <div className="absolute inset-0 flex items-center justify-center">
//             <div className="bg-white/90 p-6 md:p-10 max-w-md text-left">
//               <h3 className="text-xl md:text-2xl font-extrabold text-red-600">SMARTER DRYWALL</h3>
//               <p className="mt-2 text-gray-800 font-semibold">Boost efficiency, reduce waste, and install faster.</p>
//             </div>
//           </div>
//         </div> */}
//       </div>
//     </div>
//   );
// };

// export default NewProductsPage;
